/* tslint:disable */
import {ElementRef, Injectable, Injector, NgZone, OnDestroy} from '@angular/core';
import * as THREE from 'three';
import {MathService} from "../../math.service";
import {TabService} from "../../../tab.service";
import {MouseActionMode} from "./WebGLMouseActionsModel";
import {TrackballControls} from 'three-trackballcontrols-ts';
import {ShapeROIsService} from "../../ROIs/shape-rois.service";
import {NeuronsService} from "../../neurons/neurons.service";
import {AtlasSharedService} from "../../atlas-shared.service";
import {LayoutService} from "../../layout/layout.service";

@Injectable({
    providedIn: 'root'
})
export class WebGLService implements OnDestroy {
    // This handles the scenario where holding one keyboard letter ('s') and pressing a number key triggers a specific 3D brain rotation.
    private keysPressed: { [key: string]: boolean } = {};

    webGLVariables = {
        renderer: null,
        rendererOffsetRectangle: null,
        scene: null,

        rayCaster: null,
        viewPort: {
            canvas: null,
            size: {
                width: 0,
                height: 0
            }
        },
        lights: {
            directional: null,
            ambient: null
        },
        cameraVariables: {
            camera: null,
            controls: null,
            initialZ: 800,
            initialRotation: null
        }
    }
    mouseIntersections = [];
    referencePosition;

    sceneColor = '#000000';
    autoRotation;

    roiCenter: { screen: THREE.Vector2, openGL: THREE.Vector3 } = {
        screen: null,
        openGL: null
    };

    mouse;
    // brainIntersections = [];
    offset = new THREE.Vector3(0, 0, 0);
    intersection = new THREE.Vector3();
    plane = new THREE.Plane();

    sceneClippingPlanesDictionary = {
        top: new THREE.Plane(new THREE.Vector3(0, 1, 0), 1000),
        bottom: new THREE.Plane(new THREE.Vector3(0, -1, 0), 800),
        left: new THREE.Plane(new THREE.Vector3(-1, 0, 0), 500),
        right: new THREE.Plane(new THREE.Vector3(1, 0, 0), 500),
        front: new THREE.Plane(new THREE.Vector3(0, 0, -1), 500),
        back: new THREE.Plane(new THREE.Vector3(0, 0, 1), 500)
    };

    sceneClippingPlanesArray = [];

    predefined3DBrainRotationsArray;
    predefined3DBrainRotationsDictionary;
    current3DView;


    private frameId: number = null;


    public constructor(
        private ngZone: NgZone,
        private injector: Injector,
        private tabService: TabService,
        private mathService: MathService,
        private atlasSharedService: AtlasSharedService) {

        this.setPredefined3DBrainRotations();

        this.current3DView = {
            name: 'mapZebrain custom 3D scene',
            controls: null
        };


    }

    public ngOnDestroy(): void {
        if (this.frameId != null) {
            cancelAnimationFrame(this.frameId);
        }
    }

    public createScene(canvas: ElementRef<HTMLCanvasElement>, atlasSharedService): void {
        this.mouse = {
            isDown: false,
            position: {
                screen: null,
                openGL: null,
            },
            actionModes: {
                selectedRoi: new MouseActionMode(),
                neuronsSelection: new MouseActionMode(),

                isAnyModeActive() {
                    return ((this.selectedRoi.active) ||
                        (this.neuronsSelection.active));
                }
            }
        };

        this.autoRotation = {
            quaternion: new THREE.Quaternion(),
            rotationSpeed: 0.005,
            limitedAngle: {
                currentAngle: 0,
                targetAngle: 0
            },
            isInfinite: true,
            axis: {
                x: {vector: new THREE.Vector3(1, 0, 0), isActive: false},
                _x: {vector: new THREE.Vector3(-1, 0, 0), isActive: false},
                y: {vector: new THREE.Vector3(0, 1, 0), isActive: false},
                _y: {vector: new THREE.Vector3(0, -1, 0), isActive: false},
                z: {vector: new THREE.Vector3(0, 0, 1), isActive: false},
                _z: {vector: new THREE.Vector3(0, 0, -1), isActive: false},
            },

            isRotate() {
                return ((this.axis.x.isActive) || (this.axis._x.isActive) ||
                    (this.axis.y.isActive) || (this.axis._y.isActive) ||
                    (this.axis.z.isActive) || (this.axis._z.isActive));
            },

            rotateCamera(camera) {


                if (this.axis.x.isActive) {
                    camera.position.applyQuaternion(this.quaternion.setFromAxisAngle(this.axis.x.vector, this.rotationSpeed));
                    camera.up.applyQuaternion(this.quaternion.setFromAxisAngle(this.axis.x.vector, this.rotationSpeed));
                }

                if (this.axis._x.isActive) {
                    camera.position.applyQuaternion(this.quaternion.setFromAxisAngle(this.axis._x.vector, this.rotationSpeed));
                    camera.up.applyQuaternion(this.quaternion.setFromAxisAngle(this.axis._x.vector, this.rotationSpeed));
                }

                if (this.axis.y.isActive && atlasSharedService.screenshotState.isWaitingForScreenshot == false) {
                    camera.position.applyQuaternion(this.quaternion.setFromAxisAngle(this.axis.y.vector, this.rotationSpeed));

                    // -----------------------Added specifically for capturing Herwig regions in the video recording process---------------------
                    // atlasSharedService.take3DScreenshot(3, false);

                }

                if (this.axis._y.isActive) {
                    camera.position.applyQuaternion(this.quaternion.setFromAxisAngle(this.axis._y.vector, this.rotationSpeed));
                }

                if (this.axis.z.isActive) {
                    camera.up.applyQuaternion(this.quaternion.setFromAxisAngle(this.axis.z.vector, this.rotationSpeed));
                }

                if (this.axis._z.isActive) {
                    camera.up.applyQuaternion(this.quaternion.setFromAxisAngle(this.axis._z.vector, this.rotationSpeed));
                }
            },

            autoRotateCamera(camera, mathService) {
                if (this.isRotate()) {
                    if (this.isInfinite === false) {
                        let degreeAngle = Math.floor(mathService.radianToDegree(this.limitedAngle.currentAngle));

                        if (degreeAngle >= this.limitedAngle.targetAngle) {
                            // this.axis.x.isActive = false;
                            // this.axis._x.isActive = false;
                            // this.axis.y.isActive = false;
                            // this.axis._y.isActive = false;
                            // this.axis.z.isActive = false;
                            // this.axis._z.isActive = false;
                            // this.limitedAngle.currentAngle = 0;
                            this.reset();
                        } else {
                            this.limitedAngle.currentAngle += this.rotationSpeed;
                        }
                    }
                    this.rotateCamera(camera);
                }
            },

            reset() {
                this.axis.x.isActive = false;
                this.axis._x.isActive = false;
                this.axis.y.isActive = false;
                this.axis._y.isActive = false;
                this.axis.z.isActive = false;
                this.axis._z.isActive = false;
                this.isInfinite = true;
                this.limitedAngle.currentAngle = 0;
            }
        };

        this.webGLVariables.viewPort.size = {
            width: window.innerWidth,
            height: window.innerHeight,
        };

        // The first step is to get the reference of the canvas element from our HTML document
        this.webGLVariables.viewPort.canvas = canvas.nativeElement;

        this.sceneClippingPlanesArray = [
            this.sceneClippingPlanesDictionary.top,
            this.sceneClippingPlanesDictionary.bottom,
            this.sceneClippingPlanesDictionary.left,
            this.sceneClippingPlanesDictionary.right,
            this.sceneClippingPlanesDictionary.front,
            this.sceneClippingPlanesDictionary.back,
        ];

        this.webGLVariables.renderer = new THREE.WebGLRenderer({
            canvas: this.webGLVariables.viewPort.canvas,
            alpha: true,    // transparent background
            antialias: true, // smooth edges,
            preserveDrawingBuffer: true
        });
        this.webGLVariables.renderer.setSize(this.webGLVariables.viewPort.size.width, this.webGLVariables.viewPort.size.height);
        this.webGLVariables.renderer.setClearColor(this.sceneColor);
        this.webGLVariables.renderer.setPixelRatio(this.webGLVariables.viewPort.size.width / this.webGLVariables.viewPort.size.height);
        this.webGLVariables.renderer.sortObjects = false;
        this.webGLVariables.renderer.shadowMap.enabled = true;
        this.webGLVariables.rendererOffsetRectangle = this.webGLVariables.renderer.domElement.getBoundingClientRect();
        this.webGLVariables.renderer.clippingPlanes = this.sceneClippingPlanesArray;
        this.webGLVariables.renderer.localClippingEnabled = true;

        // create the scene
        this.webGLVariables.scene = new THREE.Scene();

        this.webGLVariables.cameraVariables.camera = new THREE.PerspectiveCamera(75, this.webGLVariables.viewPort.size.width / this.webGLVariables.viewPort.size.height, 1, 100000);
        //Controls the camera (rotation, moving and zooming) depending on mouse movement
        this.initializeControls(this.webGLVariables.cameraVariables.camera, this.webGLVariables.renderer.domElement);
        this.resetCameraControls(new THREE.Vector3(0, 0, 0));

        // soft white light
        this.webGLVariables.lights.directional = new THREE.DirectionalLight('#dddddd', 1);
        this.webGLVariables.lights.directional.position.copy(this.webGLVariables.cameraVariables.camera.position);

        this.webGLVariables.lights.ambient = new THREE.AmbientLight('#dddddd'); // soft white light

        this.webGLVariables.scene.add(this.webGLVariables.lights.ambient);
        this.webGLVariables.rayCaster = new THREE.Raycaster();

        this.roiCenter.openGL = new THREE.Vector3();
        this.roiCenter.screen = new THREE.Vector2();


        this.webGLVariables.renderer.domElement.addEventListener('mousemove', this.onDocumentMouseMove.bind(this), false);
        this.webGLVariables.renderer.domElement.addEventListener('mousedown', this.onDocumentMouseDown.bind(this), false);
        this.webGLVariables.renderer.domElement.addEventListener('mouseup', this.onDocumentMouseUp.bind(this), false);
        document.addEventListener('keyup', this.onDocumentKeyUp.bind(this), false);
        document.addEventListener('keydown', this.onDocumentKeyDown.bind(this), false);
    }

    public animate(): void {
        // We have to run this outside angular zones,
        // because it could trigger heavy changeDetection cycles.
        this.ngZone.runOutsideAngular(() => {
            if (document.readyState !== 'loading') {
                this.render();
            } else {
                window.addEventListener('DOMContentLoaded', () => {
                    this.render();
                });
            }

            window.addEventListener('resize', () => {
                this.resize();
            });
        });
    }

    rotateCamera(camera) {
        let quaternion = this.autoRotation.quaternion;
        let speed = this.autoRotation.rotationSpeed;
        if (this.autoRotation.axis.x.isActive) {
            camera.position.applyQuaternion(quaternion.setFromAxisAngle(this.autoRotation.axis.x.vector, speed));
            camera.up.applyQuaternion(quaternion.setFromAxisAngle(this.autoRotation.axis.x.vector, speed));
        }

        if (this.autoRotation.axis._x.isActive) {
            camera.position.applyQuaternion(quaternion.setFromAxisAngle(this.autoRotation.axis._x.vector, speed));
            camera.up.applyQuaternion(quaternion.setFromAxisAngle(this.autoRotation.axis._x.vector, speed));
        }

        if (this.autoRotation.axis.y.isActive) {
            camera.position.applyQuaternion(quaternion.setFromAxisAngle(this.autoRotation.axis.y.vector, speed));
        }

        if (this.autoRotation.axis._y.isActive) {
            camera.position.applyQuaternion(quaternion.setFromAxisAngle(this.autoRotation.axis._y.vector, speed));
        }

        if (this.autoRotation.axis.z.isActive) {
            camera.up.applyQuaternion(quaternion.setFromAxisAngle(this.autoRotation.axis.z.vector, speed));
        }

        if (this.autoRotation.axis._z.isActive) {
            camera.up.applyQuaternion(quaternion.setFromAxisAngle(this.autoRotation.axis._z.vector, speed));
        }
    };

    autoRotateCamera() {
        if (this.mouse.actionModes && !this.mouse.actionModes.isAnyModeActive() && this.autoRotation) {

            if (this.autoRotation.isRotate()) {
                if (this.autoRotation.isInfinite === false) {
                    let degreeAngle = Math.floor(this.mathService.radianToDegree(this.autoRotation.limitedAngle.currentAngle));

                    if (degreeAngle >= this.autoRotation.limitedAngle.targetAngle) {
                        this.autoRotation.axis.x.isActive = false;
                        this.autoRotation.axis._x.isActive = false;
                        this.autoRotation.axis.y.isActive = false;
                        this.autoRotation.axis._y.isActive = false;
                        this.autoRotation.axis.z.isActive = false;
                        this.autoRotation.axis._z.isActive = false;
                        this.autoRotation.limitedAngle.currentAngle = 0;
                    } else {
                        this.autoRotation.limitedAngle.currentAngle += this.autoRotation.rotationSpeed;
                    }
                }
                this.rotateCamera(this.webGLVariables.cameraVariables.camera);
            }
        }
    }

    public render(): void {
        this.frameId = requestAnimationFrame(() => {
            this.render();
        });

        if (this.mouse.actionModes && !this.mouse.actionModes.isAnyModeActive() && this.autoRotation) {
            this.autoRotation.autoRotateCamera(this.webGLVariables.cameraVariables.camera, this.mathService);
        }

        this.webGLVariables.cameraVariables.controls.update();
        this.webGLVariables.lights.directional.position.copy(this.webGLVariables.cameraVariables.camera.position);
        this.webGLVariables.renderer.render(this.webGLVariables.scene, this.webGLVariables.cameraVariables.camera);
    }

    public resize(): void {
        //This set time out to solve a problem
        // the problem is that the resize of the webgl renderer happens before the canvas
        // is being resized which caused shift in the mouse position
        // Now we wait until the canvas resize is done and then we resize the renderer
        setTimeout(() => {

            this.webGLVariables.viewPort.size = {
                width: window.innerWidth,
                height: window.innerHeight
            };

            this.webGLVariables.cameraVariables.camera.aspect = this.webGLVariables.viewPort.size.width / this.webGLVariables.viewPort.size.height;
            this.webGLVariables.cameraVariables.camera.updateProjectionMatrix();

            this.webGLVariables.renderer.setSize(this.webGLVariables.viewPort.size.width, this.webGLVariables.viewPort.size.height);
            this.webGLVariables.renderer.setPixelRatio(this.webGLVariables.viewPort.size.width / this.webGLVariables.viewPort.size.height);

            this.webGLVariables.rendererOffsetRectangle = this.webGLVariables.renderer.domElement.getBoundingClientRect();

            this.initializeControls(this.webGLVariables.cameraVariables.camera, this.webGLVariables.renderer.domElement);

        }, 1000);

    }

    initializeControls(camera, canvas) {
        this.webGLVariables.cameraVariables.controls = new TrackballControls(camera, canvas);
        this.webGLVariables.cameraVariables.controls.rotateSpeed = 5.0;
        this.webGLVariables.cameraVariables.controls.zoomSpeed = 5;
        this.webGLVariables.cameraVariables.controls.panSpeed = 0.8;
        this.webGLVariables.cameraVariables.controls.noZoom = false;
        this.webGLVariables.cameraVariables.controls.noPan = false;
        this.webGLVariables.cameraVariables.controls.staticMoving = true;
        this.webGLVariables.cameraVariables.controls.dynamicDampingFactor = 0.3;
    }

    public addToScene(object) {
        if (this.webGLVariables?.scene) {
            this.webGLVariables.scene.add(object);
        } else {
            console.warn('Scene is not initialized yet. Cannot add object:', object);
        }
    }

    public resetCameraControls(target = null) {

        this.webGLVariables.cameraVariables.controls.reset();
        this.webGLVariables.cameraVariables.controls.camera.position.set(0, 0, this.webGLVariables.cameraVariables.initialZ);
        // To look flipped on the Y axis because the brain model is flipped
        this.webGLVariables.cameraVariables.controls.camera.up.set(0, -1, 0);

        let targetVector = target;
        if (target === null) {
            targetVector = new THREE.Vector3(0, 0, 0);
        }
        this.webGLVariables.cameraVariables.controls.target.copy(targetVector);
        this.webGLVariables.cameraVariables.controls.update();

        this.webGLVariables.cameraVariables.initialRotation = new THREE.Euler().copy(this.webGLVariables.cameraVariables.camera.rotation);

        this.autoRotation.reset();
    };

    alignMeshToReferencePosition(mesh) {
        mesh.position.sub(this.referencePosition);
    };

    onDocumentMouseDown(event) {
        event.preventDefault();

        // this.raycaster.setFromCamera(this.mousePositionWebGL, this.webGLVariables.cameraVariables.camera);
        this.mouse.isDown = true;

        this.mouse.position.screen = new THREE.Vector2(
            event.clientX - this.webGLVariables.rendererOffsetRectangle.left,
            event.clientY - this.webGLVariables.rendererOffsetRectangle.top);

        this.mouseDownHandelROIs();
        this.mouseDownHandelNeurons();

    }

    mouseDownHandelROIs() {
        if (this.mouse.actionModes.selectedRoi.active) {
            this.roiCenter.screen = this.mouse.position.screen;
            this.roiCenter.openGL = this.getIntersectionPoint();
        } else {
            let intersectedROIMeshes = this.webGLVariables.rayCaster.intersectObjects(this.mouse.actionModes.selectedRoi.meshes);
            if (intersectedROIMeshes.length > 0) {
                let shapeROIsService = this.injector.get<ShapeROIsService>(ShapeROIsService);
                this.webGLVariables.cameraVariables.controls.enabled = false;
                this.mouse.actionModes.selectedRoi.selectedMesh = intersectedROIMeshes[0].object;
                shapeROIsService.activateROIByMesh(this.mouse.actionModes.selectedRoi.selectedMesh);

                if (this.webGLVariables.rayCaster.ray.intersectPlane(this.plane, this.intersection)) {
                    this.offset.copy(this.intersection).sub(this.mouse.actionModes.selectedRoi.selectedMesh.position);
                }
                this.webGLVariables.renderer.domElement.style.cursor = 'move';
            }
        }
    }

    mouseDownHandelNeurons() {
        if (this.mouse.actionModes.neuronsSelection.active) {
            // Get all the soma meshes that intersect with the ray from the mouse pointer
            let intersectedSomataMeshes = this.webGLVariables.rayCaster.intersectObjects(this.mouse.actionModes.neuronsSelection.meshes);

            if (intersectedSomataMeshes.length > 0) {
                let neuronsService = this.injector.get<NeuronsService>(NeuronsService);
                this.mouse.actionModes.neuronsSelection.selectedMesh = intersectedSomataMeshes[0].object;
                let selectedNeuronMeshIndex = this.mouse.actionModes.neuronsSelection.meshes.indexOf(this.mouse.actionModes.neuronsSelection.selectedMesh);
                let selectedNeuronInfo = this.mouse.actionModes.neuronsSelection.info[selectedNeuronMeshIndex];
                // neuronsService.selectNeuron(selectedNeuronInfo);
                this.atlasSharedService.scrollToNeuronElement('selected_neuron_id_' + selectedNeuronInfo.neuronID);

                // Add the highlight class to the element
                document.getElementById('neuron' + selectedNeuronInfo.neuronID)?.classList.add('highlight_neuron_div');

                // After a certain duration, remove the highlight class to revert the color
                setTimeout(() => {
                    document.getElementById('neuron' + selectedNeuronInfo.neuronID)?.classList.remove('highlight_neuron_div');
                }, 2000); // Change 2000 to the desired duration in milliseconds (2 second in this case)
            }
        }
    }

    onDocumentMouseMove(event) {

        event.preventDefault();

        this.mouse.position.screen = new THREE.Vector2(
            event.clientX - this.webGLVariables.rendererOffsetRectangle.left,
            event.clientY - this.webGLVariables.rendererOffsetRectangle.top);

        this.mouse.position.openGL = this.getMousePositionInWebGLWorld(this.mouse.position.screen, this.webGLVariables.viewPort.size);
        this.webGLVariables.rayCaster.setFromCamera(this.mouse.position.openGL, this.webGLVariables.cameraVariables.camera);

        this.mouseMoveHandelROIs();
        this.mouseMoveHandelNeurons();
    }

    mouseMoveHandelROIs() {
        let shapeROIsService = this.injector.get<ShapeROIsService>(ShapeROIsService);
        if (this.mouse.actionModes.selectedRoi.active) {
            if (this.mouse.isDown) {
                this.webGLVariables.cameraVariables.controls.enabled = false;
                if (this.roiCenter !== null) {
                    let radius = Math.floor(Math.sqrt(Math.pow(this.roiCenter.screen.x - this.mouse.position.screen.x, 2) + Math.pow(this.roiCenter.screen.y - this.mouse.position.screen.y, 2)));

                    if (!shapeROIsService.activeROI) {
                        shapeROIsService.createNewROI(this.roiCenter.openGL);
                    }
                    // shapeROIsService.updateROIRadius(shapeROIsService.activeROI);
                    shapeROIsService.updateActiveROI(this.roiCenter.openGL, radius);
                }
            }
        } else {
            if (this.mouse.actionModes.selectedRoi.selectedMesh) {
                this.webGLVariables.cameraVariables.controls.enabled = false;
                if (this.webGLVariables.rayCaster.ray.intersectPlane(this.plane, this.intersection)) {
                    this.mouse.actionModes.selectedRoi.selectedMesh.position.copy(this.intersection.sub(this.offset));
                    shapeROIsService.updateROIBackendData(shapeROIsService.activeROI);
                    // update_roi__in_2d_scene(this.shapeROIsService.activeROI);
                }
                return;
            }
            let intersected_roi_meshes = this.webGLVariables.rayCaster.intersectObjects(this.mouse.actionModes.selectedRoi.meshes);
            if (intersected_roi_meshes.length > 0) {
                if (this.mouse.actionModes.selectedRoi.intersected !== intersected_roi_meshes[0].object) {
                    this.mouse.actionModes.selectedRoi.intersected = intersected_roi_meshes[0].object;
                    this.plane.setFromNormalAndCoplanarPoint(
                        this.webGLVariables.cameraVariables.camera.getWorldDirection(this.plane.normal),
                        this.mouse.actionModes.selectedRoi.intersected.position);
                }
                this.webGLVariables.renderer.domElement.style.cursor = 'pointer';
            } else {
                this.mouse.actionModes.selectedRoi.intersected = null;
                this.webGLVariables.renderer.domElement.style.cursor = 'auto';
            }
        }
    }

    mouseMoveHandelNeurons() {
        // let neuronsService = this.injector.get<NeuronsService>(NeuronsService);
        // if (this.mouse.actionModes.neuronsSelection.active) {
        //     let intersectedSomataMeshes = this.raycaster.intersectObjects(this.mouse.actionModes.neuronsSelection.meshes);
        //
        //     if (intersectedSomataMeshes.length > 0) {
        //         if (this.mouse.actionModes.neuronsSelection.intersected !== intersectedSomataMeshes[0].object) {
        //             this.mouse.actionModes.neuronsSelection.intersected = intersectedSomataMeshes[0].object;
        //             this.plane.setFromNormalAndCoplanarPoint(
        //                 this.webGLVariables.cameraVariables.camera.getWorldDirection(this.plane.normal),
        //                 this.mouse.actionModes.neuronsSelection.intersected.position);
        //         }
        //         this.renderer.domElement.style.cursor = 'pointer';
        //
        //     } else {
        //         this.mouse.actionModes.neuronsSelection.intersected = null;
        //         this.renderer.domElement.style.cursor = 'auto';
        //     }
        // }
    }

    mouseMoveHandelRectangularROIs() {
        let shapeROIsService = this.injector.get<ShapeROIsService>(ShapeROIsService);
        if (this.mouse.actionModes.selectedRoi.active) {
            if (this.mouse.isDown) {
                this.webGLVariables.cameraVariables.controls.enabled = false;
                if (this.roiCenter !== null) {
                    let radius = Math.floor(Math.sqrt(Math.pow(this.roiCenter.screen.x - this.mouse.position.screen.x, 2) + Math.pow(this.roiCenter.screen.y - this.mouse.position.screen.y, 2)));

                    if (!shapeROIsService.activeROI) {
                        shapeROIsService.createNewROI(this.roiCenter.openGL);
                    }
                    // shapeROIsService.updateROIRadius(shapeROIsService.activeROI);
                    shapeROIsService.updateActiveROI(this.roiCenter.openGL, radius);
                }
            }
        } else {
            if (this.mouse.actionModes.selectedRoi.selectedMesh) {
                this.webGLVariables.cameraVariables.controls.enabled = false;
                if (this.webGLVariables.rayCaster.ray.intersectPlane(this.plane, this.intersection)) {
                    this.mouse.actionModes.selectedRoi.selectedMesh.position.copy(this.intersection.sub(this.offset));
                    shapeROIsService.updateROIBackendData(shapeROIsService.activeROI);
                    // update_roi__in_2d_scene(this.shapeROIsService.activeROI);
                }
                return;
            }
            let intersected_roi_meshes = this.webGLVariables.rayCaster.intersectObjects(this.mouse.actionModes.selectedRoi.meshes);
            if (intersected_roi_meshes.length > 0) {
                if (this.mouse.actionModes.selectedRoi.intersected !== intersected_roi_meshes[0].object) {
                    this.mouse.actionModes.selectedRoi.intersected = intersected_roi_meshes[0].object;
                    this.plane.setFromNormalAndCoplanarPoint(
                        this.webGLVariables.cameraVariables.camera.getWorldDirection(this.plane.normal),
                        this.mouse.actionModes.selectedRoi.intersected.position);
                }
                this.webGLVariables.renderer.domElement.style.cursor = 'pointer';
            } else {
                this.mouse.actionModes.selectedRoi.intersected = null;
                this.webGLVariables.renderer.domElement.style.cursor = 'auto';
            }
        }
    }

    getIntersectionPoint() {
        this.mouse.position.openGL = this.getMousePositionInWebGLWorld(this.mouse.position.screen, this.webGLVariables.viewPort.size);
        this.webGLVariables.rayCaster.setFromCamera(this.mouse.position.openGL, this.webGLVariables.cameraVariables.camera);

        let intersects = this.webGLVariables.rayCaster.intersectObjects(this.mouseIntersections);
        if (intersects.length > 0) {
            return intersects[0].point;
        }

        return null;
    }

    onDocumentMouseUp(event) {

        event.preventDefault();

        this.webGLVariables.cameraVariables.controls.enabled = true;
        this.mouse.isDown = false;

        this.mouseUpHandelROIs();

        this.mouseUpHandelNeurons()

        this.webGLVariables.renderer.domElement.style.cursor = 'auto';
    }

    mouseUpHandelROIs() {

        if (this.mouse.actionModes.selectedRoi.selectedMesh) {
            this.mouse.actionModes.selectedRoi.selectedMesh = null;
        }
    }

    mouseUpHandelNeurons() {
        if (this.mouse.actionModes.neuronsSelection.intersected) {
            this.mouse.actionModes.neuronsSelection.selectedMesh = null;
        }
    }

    onDocumentKeyUp(event) {
        this.keysPressed[event.key] = false;

        if (event.target.type === 'text') {
            return;
        }
        if (event.key === 'Control') {
            this.mouse.actionModes.neuronsSelection.active = false;
        }
        if (event.key === 'Shift') {
            this.mouse.actionModes.selectedRoi.active = false;
        }
        if (event.key === 'r') {
            let layoutService = this.injector.get<LayoutService>(LayoutService);
            if (layoutService.viewOptions.threeD) {
                this.resetCameraControls();
            }
        }
        if (event.key === 'c') {
            this.loadCameraView(this.predefined3DBrainRotationsDictionary.coronal.data);
        }

    }

    onDocumentKeyDown(event) {
        this.keysPressed[event.key] = true;

        if (event.target.type === 'text') {
            return;
        }
        if (event.key === 'Control') {
            this.mouse.actionModes.neuronsSelection.active = true;
        }
        if (event.key === 'Shift') {
            this.mouse.actionModes.selectedRoi.active = true;
            this.tabService.openROIsTab();
        }

        // Check if 's' is held down and a number key (1-4) is pressed
        if (this.keysPressed['s']) {
            switch (event.key) {
                case '1':
                    this.loadCameraView(this.predefined3DBrainRotationsDictionary.sagittalHorizontalLeft.data);
                    break;
                case '2':
                    this.loadCameraView(this.predefined3DBrainRotationsDictionary.sagittalHorizontalRight.data);
                    break;
                case '3':
                    this.loadCameraView(this.predefined3DBrainRotationsDictionary.sagittalVerticalRight.data);
                    break;
                case '4':
                    this.loadCameraView(this.predefined3DBrainRotationsDictionary.sagittalVerticalLeft.data);
                    break;
            }
        }
    }

    addMeshToScene(mesh) {
        this.webGLVariables.scene.add(mesh);
    }

    getMousePositionInWebGLWorld(mousePositionOnScreen, viewportSize) {
        let result = new THREE.Vector2();
        result.x = (mousePositionOnScreen.x / viewportSize.width) * 2 - 1;
        result.y = -(mousePositionOnScreen.y / viewportSize.height) * 2 + 1;
        return result;
    }

    // This function was moved from 3Dview_visualization_options.component to webgl.service for easier access and to enable 3D brain rotation shortcuts.
    setPredefined3DBrainRotations() {
        this.predefined3DBrainRotationsDictionary = {
            dorsal: {
                name: 'Dorsal',
                data: {
                    position: {
                        x: 0,
                        y: 0,
                        z: this.webGLVariables.cameraVariables.initialZ,
                    },
                    rotation: {
                        x: 0,
                        y: 0,
                        z: 0,
                    },
                    up: {
                        x: 0,
                        y: -1,
                        z: 0,
                    },
                }
            },

            sagittalVerticalLeft: {
                name: 'Sagittal (Vertical-Left)',
                data: {
                    position: {x: -800, y: 0, z: -3.3629286597501564},
                    rotation: {_x: -3.141592653589793, _y: -1.5665926535898074, _z: 0, _order: 'XYZ'},
                    up: {x: 0, y: -1, z: 0}
                }
            },

            sagittalVerticalRight: {
                name: 'Sagittal (Vertical-Right)',
                data: {
                    position: {x: 800, y: 0, z: -3.3629286597501564},
                    rotation: {_x: -3.141592653589793, _y: 1.5665926535898074, _z: 0, _order: 'XYZ'},
                    up: {x: 0, y: -1, z: 0}
                }
            },

            sagittalHorizontalLeft: {
                name: 'Sagittal (Horizontal-Left)',
                data: {
                    position: {x: 800, y: -3.336355035625822, z: -0.4219291964596052},
                    rotation: {_x: 1.696592653589831, _y: 1.5665926535898336, _z: 0, _order: 'XYZ'},
                    up: {x: 0, y: 0, z: 1}
                }
            },

            sagittalHorizontalRight: {
                name: 'Sagittal (Horizontal-Right)',
                data: {
                    position: {x: -800, y: -3.362898946859302, z: 0.014136611463213579},
                    rotation: {_x: 1.5665926535898207, _y: -1.5665926535898336, _z: 0, _order: "XYZ"},
                    up: {x: 0, y: 0, z: 1}
                }
            },

            coronal: {
                name: 'Coronal',
                data: {
                    position: {x: -6.725797893718429, y: -800, z: -10.088191732454657},
                    rotation: {
                        _x: 1.5834073464102072,
                        _y: -0.008407346410206313,
                        _z: 1.7347847859230342e-17,
                        _order: "XYZ"
                    },
                    up: {x: 0, y: 0, z: 1}
                }
            }
        };

        this.predefined3DBrainRotationsArray = [
            this.predefined3DBrainRotationsDictionary.dorsal,
            this.predefined3DBrainRotationsDictionary.sagittalVerticalLeft,
            this.predefined3DBrainRotationsDictionary.sagittalVerticalRight,
            this.predefined3DBrainRotationsDictionary.sagittalHorizontalLeft,
            this.predefined3DBrainRotationsDictionary.sagittalHorizontalRight,
            this.predefined3DBrainRotationsDictionary.coronal
        ];
    }

    // This function was moved from 3Dview_visualization_options.component to webgl.service for easier access and to enable 3D brain rotation shortcuts.
    loadCameraView(data, isSharedCurrentViewData = false) {

        let position = isSharedCurrentViewData ? data.rotations.position : data.position;
        this.current3DView.controls.camera.position.copy(position);

        let up = isSharedCurrentViewData ? data.rotations.up : data.up;
        this.current3DView.controls.camera.up.copy(up);

        // // Mimi commented these three lines because they are not reflecting on the 3D view
        // this.currentView.controls.camera.rotation.x = jsonViewData.rotation._x;
        // this.currentView.controls.camera.rotation.y = jsonViewData.rotation._y;
        // this.currentView.controls.camera.rotation.z = jsonViewData.rotation._z;
        this.current3DView.controls.update();
    }

    // This function was moved from 3Dview_visualization_options.component to webgl.service for easier access and to enable 3D brain rotation shortcuts.
    updateCurrent3DViewControls() {
        this.current3DView.controls = this.webGLVariables.cameraVariables.controls;
        this.atlasSharedService.sharing3DViewData['rotations']['position'] = this.current3DView.controls.camera.position;
        this.atlasSharedService.sharing3DViewData['rotations']['up'] = this.current3DView.controls.camera.up;
    }
}
